import React, { useState, useEffect } from 'react';
import { Switch} from '@material-ui/core';
import { useDispatch, useSelector} from 'react-redux'
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Divider } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import _ from 'lodash';
import { openEditDriverModal, showAlertSnackbar } from '../../../reducers/uiSlice';
import { getDriverList, getBasicInfo, getWorkingArea, handleAddEditDriverDialog, handleDriverId } from '../../../actions/driverActions';
import { useTranslation } from 'react-i18next';
import { getUserPreference } from "../../../utilities/common";
import AddEditDriver from '../AddEditDriver';
import { getProvinceCountryByWarehouse, switchSortingScan } from '../../../actions/driverAppApprovalActions';
import { useToken } from '../../../utilities/hooks';
import ConditionalSearch from './ConditionalSearch';
import { DataGrid } from '@material-ui/data-grid';

const useStyles = makeStyles((theme)=> ({
    root: {
        flexGrow: 1,
    },
    paper: {
        padding: theme.spacing(2),    
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },  
    searchFab: {
        color: green[500],
        width: 40, 
        height: 40
    },
    table: {
        minWidth: 700,
        border: 1,    
    },  
}))

const ADVANCED_EDIT = 304;

export default function DriverSearchList() {
    const classes = useStyles();
    const {t} = useTranslation('editDriver');
    const dispatch = useDispatch();
    const openModal = useSelector(openEditDriverModal) || false;
    let userPref = getUserPreference();
    const tokenContents = useToken();

    const userHasAdvancedEdit = tokenContents.uiAbilities.includes(ADVANCED_EDIT);

    const DEFAULT_CITY_ID = 1;
    const DEFAULT_DRIVER_TYPE = 9;
    const DEFAULT_DRIVER_STATUS = 9;
    const SERVICE_NUMBER = 1;
    const FULL_TIME_DRIVER = -1;
    const SELF_EMPLOYEE_DRIVER = 0;
    const SORTING_WORKER = 10;
    const DEFAULT_START_PAGE = 0;
    const DEFAULT_ROWS_PER_PAGE = 20;
    const DRIVER_TYPE_ALL = 9;
    const IS_HIRED_ALL = 9;

    const columns = [
        {
            field: 'id', 
            headerName: t('id'), 
            headerAlign: 'center',
            flex: 1,
            sortable: false,
            align: 'center'
        },
        {
            field: 'name', 
            headerName: t('user_name'), 
            headerAlign: 'center',
            flex: 1,
            sortable: false,
            align: 'center'
        },
        {
            field: 'phone', 
            headerName: t('phone'), 
            flex: 1,
            headerAlign: 'center', 
            sortable: false,
            align: 'center'
        },
        {
            field: 'firstname', 
            headerName: t('first_name'), 
            flex: 1, 
            headerAlign: 'center', 
            sortable: false,
            align: 'center'
        },
        {
            field: 'lastname', 
            headerName: t('last_name'), 
            flex: 1, 
            headerAlign: 'center', 
            sortable: false,
            align: 'center'
        },
        {
            field: 'lngLat',
            headerName: t('sorting_scan'),
            sortable: false,
            flex: 1,
            headerAlign: 'center', 
            align: 'center',
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex', 
                            justifyContent: 'space-evenly', 
                            alignItems: 'center'
                        }}
                    >
                        <Switch 
                            checked={params.row.bossflg}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSortingChange(params.row.id, params.row.bossflg, params.row.role);
                            }}
                        />
                    </div>
                )
            }
        }
    ];
 
    const driverList = useSelector(state => state.driver.driverList) || [];
    const driverIdAdded = useSelector(state => state.driver.driverId);
    const pageTotal = useSelector(state => state.driver.pageTotal) || 0;

    const [driverId,setDriverId] = useState("");
    const [cityId,setCityId] = useState("");
    const [loading, setLoading] = useState(false);
    const [driverSalaryState,setDriverSalaryState] = useState([]);
    const [driverSalaryTypeFixList,setDriverSalaryTypeFixList] = useState([]);
    const [page, setPage] = useState(0);

    const [searchDriverId, setSearchDriverId] = useState("");
    const [searchDriverPhone, setSearchDriverPhone] = useState("");
    const [driverType, setDriverType] = useState(DEFAULT_DRIVER_TYPE);
    const [driverStatus, setDriverStatus] = useState(DEFAULT_DRIVER_STATUS);
    
    useEffect(() => {
        if(openModal) {
            setLoading(true);
            dispatch(getBasicInfo())
            .then((res) => {
                initSalaryType(res.data.salay_type);
                setLoading(false);
            })
            dispatch(getWorkingArea());     
            dispatch(getProvinceCountryByWarehouse(userPref.userCity))  
        }
    },[]);

    // Search driver id right after adding a new driver
    useEffect(() => {
    if(driverIdAdded) {
        handleSearch(
            userPref.userCity,
            DEFAULT_DRIVER_TYPE,
            DEFAULT_DRIVER_STATUS,
            driverIdAdded,
            "",
            DEFAULT_START_PAGE,
            DEFAULT_ROWS_PER_PAGE
        );
    }
    }, [driverIdAdded])

    return (
        <>
            <ConditionalSearch 
                handleSearch={handleSearch}
            />
            <Divider></Divider>
            <DataGrid
                columns={columns}
                rows={driverList}
                pagination
                paginationMode="server"
                pageSize={20}
                alignItems="center"
                page={page - 1}
                rowsPerPageOptions={[DEFAULT_ROWS_PER_PAGE]}
                rowCount={pageTotal}
                onPageChange={(newPage) => {
                    setPage(newPage + 1);
                    handleSearch(
                        userPref.userCity,
                        driverType,
                        driverStatus,
                        searchDriverId,
                        searchDriverPhone,
                        newPage,
                        DEFAULT_ROWS_PER_PAGE
                    )
                }}
                style={{height: '70vh', margin: 16}}
                onRowClick={(selectedRow) => {
                    if (userHasAdvancedEdit) {
                        handleOpenEditDriver(selectedRow.row.id, selectedRow.row.city_id)
                    } else {
                        dispatch(showAlertSnackbar({message: t('common:access_denied'), type: 'error'}))
                    }
                }}
            />
            <AddEditDriver 
                driverId={driverId} 
                cityId={cityId} 
                driverSalaryState={driverSalaryState} 
                driverSalaryTypeFixList={driverSalaryTypeFixList}
            />
        </>
    );

    function initSalaryType(salaryTypeList) {
        let salaryList = [];

        // set FIX salary type
        const fixList = salaryTypeList.filter((value) => value.indexOf("FIX_") >= 0)
        .map(type => ({label: type, value: type}))
        .sort((a,b) => a.value.localeCompare(b.value));
        if (fixList && fixList.length > 0) {
            salaryList.push({label: "FIX", value: "FIX" , checked: false});
        }

        // set NON FIX salary type
        salaryTypeList.filter((value)=>value.indexOf("FIX_") < 0).forEach(item => {
            salaryList.push({label: item, value: item, checked: false});
        });
        
        setDriverSalaryTypeFixList(fixList);
        setDriverSalaryState(salaryList);
    }

    function handleSortingChange(driverId, scanEnable, role) {
        if(role === SORTING_WORKER) {
            dispatch(showAlertSnackbar({message: t('fail_sorter'), type: 'warning'}))
        } else {
            setLoading(true);
            dispatch(switchSortingScan(driverId, scanEnable ? 0 : 1))
            .then(() => {
                dispatch(handleDriverId(driverId));
                dispatch(showAlertSnackbar({message: t('success_switch'), type: 'success'}))
                setLoading(false);
            })
            .catch(() => {
                dispatch(showAlertSnackbar({message: t('fail_switch'), type: 'error'}))
                setLoading(false);
            })
        }
    }

    function handleSearch(
        branch,
        type = 9,
        status = 9,
        driverId = -1,
        driverPhone = "",
        startPage = DEFAULT_START_PAGE,
        pageSize = DEFAULT_ROWS_PER_PAGE,
        searchType
    ){    
        setSearchDriverId(driverId);
        setSearchDriverPhone(driverPhone);
        setDriverType(type);
        setDriverStatus(status);

        if(searchType === 'conditional_search') {
            startPage = 0;
        }

        let searchParams = {
            branch: branch, 
            page: startPage + 1, 
            size: pageSize
        };

        if (type === SERVICE_NUMBER ) {  //服务号
            searchParams['virtual'] = SERVICE_NUMBER;
        } else if (type === FULL_TIME_DRIVER) {  //真实司机(9开头)
            searchParams['role'] = FULL_TIME_DRIVER;
        } else if (type === SELF_EMPLOYEE_DRIVER) { //真实司机(非9开头)
            searchParams['role'] = SELF_EMPLOYEE_DRIVER;
        } else if(type === SORTING_WORKER) { // 分拣员
            searchParams['role'] = SORTING_WORKER;
        } else {} //全部

        if (status !== IS_HIRED_ALL) { 
            searchParams['is_hired'] = status;
        } 

        if (driverId) {
            searchParams['driver_id'] = driverId;
        }

        if (driverPhone) {
            searchParams['phone'] = driverPhone;
        }

        dispatch(getDriverList(searchParams));
    }

    function handleOpenEditDriver(driverId,cityId) {
        setDriverId(driverId);
        setCityId(cityId);
        dispatch(handleAddEditDriverDialog(true, 'update'));
    }
}
